<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-copy text-orange-500 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Doublons de visites</h1>
              <p class="text-sm text-gray-500 mt-1">
                Identification et suppression des visites en doublon (même lot, ±15 jours d'écart)
              </p>
            </div>
          </div>

          <!-- Filtre de date + bouton analyser -->
          <div class="flex items-center gap-3">
            <div class="flex flex-col items-end gap-1">
              <label for="duplicatesFromDate" class="text-xs font-medium text-gray-600 whitespace-nowrap">
                <i class="fas fa-calendar-day text-orange-400 mr-1"></i>
                Doublons à partir du
              </label>
              <input
                id="duplicatesFromDate"
                v-model="duplicatesFromDate"
                type="date"
                class="block w-44 px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <button
              @click="analyzeDatabase"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'" class="mr-2"></i>
              {{ loading ? 'Analyse en cours...' : 'Analyser la base' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- État initial (avant analyse) -->
      <div v-if="!analyzed && !loading" class="text-center py-16">
        <i class="fas fa-search text-gray-300 text-6xl mb-4"></i>
        <p class="text-gray-500 text-lg mb-2">Aucune analyse effectuée</p>
        <p class="text-gray-400 text-sm">
          Cliquez sur "Analyser la base" pour détecter les visites en doublon dans MSSQL.
        </p>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="text-center py-16">
        <i class="fas fa-spinner fa-spin text-orange-500 text-5xl mb-4"></i>
        <p class="text-gray-600">Analyse de la base MSSQL en cours...</p>
      </div>

      <!-- Erreur -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <i class="fas fa-exclamation-circle text-red-400 text-xl flex-shrink-0 mt-0.5"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Erreur lors de l'analyse</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Résultats -->
      <template v-else-if="analyzed">

        <!-- Bandeau de synthèse -->
        <div class="mb-6 rounded-lg p-4 flex items-center justify-between"
          :class="duplicateGroups.length > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'">
          <div class="flex items-center">
            <i
              :class="duplicateGroups.length > 0 ? 'fas fa-exclamation-triangle text-orange-500' : 'fas fa-check-circle text-green-500'"
              class="text-2xl mr-3"
            ></i>
            <div>
              <p class="font-semibold" :class="duplicateGroups.length > 0 ? 'text-orange-800' : 'text-green-800'">
                <template v-if="duplicateGroups.length > 0">
                  {{ duplicateGroups.length }} groupe{{ duplicateGroups.length > 1 ? 's' : '' }} de doublon{{ duplicateGroups.length > 1 ? 's' : '' }} détecté{{ duplicateGroups.length > 1 ? 's' : '' }}
                  —
                  {{ totalDuplicateVisits }} visite{{ totalDuplicateVisits > 1 ? 's' : '' }} concernée{{ totalDuplicateVisits > 1 ? 's' : '' }}
                </template>
                <template v-else>
                  Aucun doublon détecté — la base est propre !
                </template>
              </p>
              <p class="text-sm mt-0.5" :class="duplicateGroups.length > 0 ? 'text-orange-700' : 'text-green-700'">
                <template v-if="duplicateGroups.length > 0">
                  Pour chaque groupe, sélectionnez la visite à conserver puis supprimez les doublons.
                  <span v-if="analyzedFromDateLabel" class="font-medium">
                    (analyse à partir du {{ analyzedFromDateLabel }})
                  </span>
                </template>
                <template v-else>
                  Toutes les visites sont uniques pour chaque lot.
                  <span v-if="analyzedFromDateLabel" class="font-medium">
                    (à partir du {{ analyzedFromDateLabel }})
                  </span>
                </template>
              </p>
            </div>
          </div>

          <!-- Bouton "Tout nettoyer" -->
          <button
            v-if="duplicateGroups.length > 0"
            @click="cleanAllDuplicates"
            :disabled="deletingAll"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ml-4"
          >
            <i :class="deletingAll ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt'" class="mr-2"></i>
            {{ deletingAll ? 'Nettoyage...' : 'Tout nettoyer' }}
          </button>
        </div>

        <!-- Liste des groupes de doublons -->
        <div v-if="duplicateGroups.length > 0" class="space-y-6">
          <div
            v-for="(group, groupIndex) in duplicateGroups"
            :key="`group-${group.property.id}`"
            class="bg-white rounded-lg shadow overflow-hidden"
          >
            <!-- En-tête du groupe (lot) -->
            <div class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 class="text-base font-semibold text-gray-900">
                  <i class="fas fa-home text-gray-400 mr-2"></i>
                  <span class="font-bold text-gray-700">{{ group.property.nsil || 'NSIL inconnu' }}</span>
                  <span v-if="group.property.street" class="text-gray-500 font-normal ml-2">
                    — {{ group.property.street }}{{ group.property.city ? ', ' + group.property.city : '' }}{{ group.property.zipCode ? ' (' + group.property.zipCode + ')' : '' }}
                  </span>
                </h3>
                <p class="text-sm text-gray-500 mt-0.5">
                  {{ group.count }} visites en doublon
                </p>
              </div>
              <button
                @click="deleteGroupDuplicates(group, groupIndex)"
                :disabled="group.deleting"
                class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i :class="group.deleting ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt'" class="mr-1.5"></i>
                {{ group.deleting ? 'Suppression...' : 'Supprimer les doublons' }}
              </button>
            </div>

            <!-- Liste des visites du groupe -->
            <div class="divide-y divide-gray-100">
              <div
                v-for="(visit, visitIndex) in group.visits"
                :key="visit.id"
                class="px-6 py-4 flex items-center gap-4"
                :class="isVisitToKeep(group, visit) ? 'bg-green-50' : 'bg-white'"
              >
                <!-- Radio button : visite à conserver -->
                <div class="flex-shrink-0">
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      :name="`keep-group-${groupIndex}`"
                      :value="visit.id"
                      v-model="group.visitToKeepId"
                      class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <span class="ml-2 text-sm text-gray-600 whitespace-nowrap">
                      <span v-if="isVisitToKeep(group, visit)" class="text-green-700 font-medium">Conserver</span>
                      <span v-else class="text-red-500">Supprimer</span>
                    </span>
                  </label>
                </div>

                <!-- Badge "à supprimer" / "à conserver" -->
                <div class="flex-shrink-0">
                  <span
                    v-if="isVisitToKeep(group, visit)"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                  >
                    <i class="fas fa-check mr-1"></i> À conserver
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                  >
                    <i class="fas fa-times mr-1"></i> Doublon
                  </span>
                </div>

                <!-- Informations de la visite -->
                <div class="flex-1 min-w-0">
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <!-- Date/heure -->
                    <span class="text-gray-900 font-medium">
                      <i class="fas fa-calendar-alt text-gray-400 mr-1"></i>
                      {{ formatDateTime(visit.createdAt) }}
                    </span>
                    <!-- Auteur -->
                    <span class="text-gray-600">
                      <i class="fas fa-user text-gray-400 mr-1"></i>
                      {{ visit.managerName || 'Auteur inconnu' }}
                    </span>
                    <!-- Relouable -->
                    <span v-if="visit.rentable" class="text-gray-600">
                      <i class="fas fa-tag text-gray-400 mr-1"></i>
                      {{ visit.rentable }}
                    </span>
                  </div>
                  <!-- UUID (aide au debug, collapsible) -->
                  <p class="text-xs text-gray-400 mt-0.5 font-mono truncate">{{ visit.id }}</p>
                </div>

                <!-- Indicateur de numéro d'ordre -->
                <div class="flex-shrink-0 text-xs text-gray-400">
                  #{{ visitIndex + 1 }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Logs Panel -->
      <div v-if="logs.length > 0" class="mt-8 bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Journal des opérations</h2>
          <button @click="clearLogs" class="text-sm text-gray-600 hover:text-gray-900">
            Effacer
          </button>
        </div>
        <div class="px-6 py-4 max-h-72 overflow-y-auto">
          <div v-for="(log, index) in logs" :key="index" class="flex items-start py-1.5 text-sm">
            <span class="text-gray-400 mr-3 flex-shrink-0 font-mono">{{ log.time }}</span>
            <i
              v-if="log.type === 'success'" class="fas fa-check-circle text-green-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else-if="log.type === 'error'" class="fas fa-times-circle text-red-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else class="fas fa-info-circle text-blue-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <span :class="log.type === 'error' ? 'text-red-700' : 'text-gray-700'">{{ log.message }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Modal de confirmation "Tout nettoyer" -->
    <div
      v-if="showCleanAllModal"
      class="fixed z-50 inset-0 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-50 z-40" @click="showCleanAllModal = false"></div>
        <div class="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full z-50 mx-4">
          <div class="bg-white px-6 pt-6 pb-4">
            <div class="flex items-start">
              <div class="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <i class="fas fa-exclamation-triangle text-red-600 text-xl"></i>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-medium text-gray-900">Confirmer le nettoyage global</h3>
                <p class="mt-2 text-sm text-gray-600">
                  Cette opération va supprimer définitivement
                  <strong>{{ totalDuplicatesToDelete }} visite{{ totalDuplicatesToDelete > 1 ? 's' : '' }}</strong>
                  de doublon dans MSSQL et MySQL<template v-if="analyzedFromDateLabel"> (doublons depuis le <strong>{{ analyzedFromDateLabel }}</strong>)</template>.
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  Pour chaque groupe, la visite sélectionnée avec la radio "Conserver" sera conservée.
                  La plus récente est conservée par défaut.
                </p>
                <p class="mt-2 text-sm text-red-600 font-medium">⚠️ Cette action est irréversible.</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex justify-end gap-3">
            <button
              @click="showCleanAllModal = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
            >
              Annuler
            </button>
            <button
              @click="confirmCleanAll"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none"
            >
              <i class="fas fa-trash-alt mr-1.5"></i>
              Supprimer les doublons
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import moment from 'moment'

export default {
  name: 'DoublonsVisites',

  data() {
    return {
      // État de l'analyse
      loading: false,
      analyzed: false,
      error: null,
      // Filtre de date : doublons à partir de cette date (format YYYY-MM-DD)
      // Défaut : 1er janvier de l'année en cours
      duplicatesFromDate: `${new Date().getFullYear()}-01-01`,
      // Date utilisée pour la dernière analyse (pour l'affichage)
      analyzedFromDate: null,
      // Groupes de doublons
      duplicateGroups: [],
      // Suppression globale
      deletingAll: false,
      showCleanAllModal: false,
      // Logs
      logs: [],
    }
  },

  computed: {
    /**
     * Nombre total de visites concernées par des doublons
     */
    totalDuplicateVisits() {
      return this.duplicateGroups.reduce((sum, g) => sum + g.count, 0)
    },

    /**
     * Nombre total de visites qui seront supprimées (toutes sauf celle à conserver)
     */
    totalDuplicatesToDelete() {
      return this.duplicateGroups.reduce((sum, g) => sum + (g.count - 1), 0)
    },

    /**
     * Date de l'analyse courante, formatée en DD/MM/YYYY pour l'affichage
     */
    analyzedFromDateLabel() {
      if (!this.analyzedFromDate) return null
      return moment(this.analyzedFromDate).format('DD/MM/YYYY')
    },
  },

  methods: {
    /**
     * Lance l'analyse de la base MSSQL pour détecter les doublons
     */
    async analyzeDatabase() {
      this.loading = true
      this.error = null
      this.analyzed = false
      this.analyzedFromDate = null
      this.duplicateGroups = []

      const fromDate = this.duplicatesFromDate || null
      const fromDateLabel = fromDate ? moment(fromDate).format('DD/MM/YYYY') : null

      this.addLog('info', fromDateLabel
        ? `Lancement de l'analyse des doublons de visites à partir du ${fromDateLabel}...`
        : 'Lancement de l\'analyse des doublons de visites (toutes dates)...'
      )

      try {
        const response = await api.getVisitDuplicates(fromDate)

        if (!response.success) {
          throw new Error(response.message || response.error || 'Erreur inconnue')
        }

        // Mémoriser la date utilisée pour l'affichage
        this.analyzedFromDate = fromDate

        // Enrichir chaque groupe avec l'état réactif nécessaire
        this.duplicateGroups = (response.data || []).map(group => ({
          ...group,
          // Par défaut : conserver la visite la plus récente (dernière en ordre ASC)
          visitToKeepId: group.visits[group.visits.length - 1]?.id || null,
          deleting: false,
        }))

        this.analyzed = true

        if (this.duplicateGroups.length > 0) {
          this.addLog('info', `${this.duplicateGroups.length} groupe(s) de doublons trouvé(s) — ${this.totalDuplicateVisits} visite(s) concernée(s)${fromDateLabel ? ' depuis le ' + fromDateLabel : ''}`)
        } else {
          this.addLog('success', `Aucun doublon détecté — la base est propre !${fromDateLabel ? ' (depuis le ' + fromDateLabel + ')' : ''}`)
        }
      } catch (err) {
        this.error = err.message || 'Impossible d\'analyser la base de données'
        this.addLog('error', `Erreur : ${this.error}`)
      } finally {
        this.loading = false
      }
    },

    /**
     * Retourne true si la visite est celle sélectionnée pour être conservée
     */
    isVisitToKeep(group, visit) {
      return group.visitToKeepId === visit.id
    },

    /**
     * Supprime les visites en doublon d'un groupe (toutes sauf celle à conserver)
     */
    async deleteGroupDuplicates(group, groupIndex) {
      const toDelete = group.visits.filter(v => v.id !== group.visitToKeepId)

      if (toDelete.length === 0) {
        this.addLog('info', `Lot ${group.property.nsil} : aucun doublon à supprimer`)
        return
      }

      if (!confirm(
        `Supprimer ${toDelete.length} visite(s) en doublon pour le lot ${group.property.nsil || group.property.id} ?\n` +
        `La visite conservée est celle du ${this.formatDateTime(group.visits.find(v => v.id === group.visitToKeepId)?.createdAt)}.\n\n` +
        `⚠️ Cette action est irréversible.`
      )) {
        return
      }

      group.deleting = true
      this.addLog('info', `Suppression des doublons pour le lot ${group.property.nsil}...`)

      let successCount = 0
      let errorCount = 0

      for (const visit of toDelete) {
        try {
          const result = await api.deleteVisitDuplicate(visit.id)
          if (result.success) {
            successCount++
            this.addLog('success', `✓ Visite supprimée : ${this.formatDateTime(visit.createdAt)} par ${visit.managerName || 'inconnu'} (lot ${group.property.nsil})`)
          } else {
            errorCount++
            this.addLog('error', `✗ Échec suppression visite ${visit.id} : ${result.message}`)
          }
        } catch (err) {
          errorCount++
          this.addLog('error', `✗ Erreur visite ${visit.id} : ${err.message}`)
        }
      }

      group.deleting = false

      if (errorCount === 0) {
        // Retirer le groupe de la liste
        this.duplicateGroups.splice(groupIndex, 1)
        this.addLog('success', `✅ Lot ${group.property.nsil} : ${successCount} doublon(s) supprimé(s) avec succès`)
      } else {
        this.addLog('error', `⚠️ Lot ${group.property.nsil} : ${successCount} succès, ${errorCount} erreur(s)`)
      }
    },

    /**
     * Ouvre le modal de confirmation pour tout nettoyer
     */
    cleanAllDuplicates() {
      if (this.duplicateGroups.length === 0) return
      this.showCleanAllModal = true
    },

    /**
     * Confirme et lance le nettoyage global
     */
    async confirmCleanAll() {
      this.showCleanAllModal = false
      this.deletingAll = true
      this.addLog('info', `🚀 Nettoyage global — ${this.totalDuplicatesToDelete} visite(s) à supprimer dans ${this.duplicateGroups.length} groupe(s)...`)

      let globalSuccess = 0
      let globalError = 0

      // Traiter les groupes en séquence (pour ne pas surcharger l'API)
      const groupsCopy = [...this.duplicateGroups]
      for (let i = groupsCopy.length - 1; i >= 0; i--) {
        const group = groupsCopy[i]
        const toDelete = group.visits.filter(v => v.id !== group.visitToKeepId)

        if (toDelete.length === 0) continue

        group.deleting = true
        let groupError = false

        for (const visit of toDelete) {
          try {
            const result = await api.deleteVisitDuplicate(visit.id)
            if (result.success) {
              globalSuccess++
              this.addLog('success', `✓ Visite supprimée — lot ${group.property.nsil} du ${this.formatDateTime(visit.createdAt)}`)
            } else {
              globalError++
              groupError = true
              this.addLog('error', `✗ Échec : lot ${group.property.nsil}, visite ${visit.id} — ${result.message}`)
            }
          } catch (err) {
            globalError++
            groupError = true
            this.addLog('error', `✗ Erreur : lot ${group.property.nsil}, visite ${visit.id} — ${err.message}`)
          }
        }

        group.deleting = false

        if (!groupError) {
          // Retirer le groupe traité avec succès
          const idx = this.duplicateGroups.findIndex(g => g.property.id === group.property.id)
          if (idx !== -1) {
            this.duplicateGroups.splice(idx, 1)
          }
        }
      }

      this.deletingAll = false

      if (globalError === 0) {
        this.addLog('success', `🎉 Nettoyage terminé — ${globalSuccess} doublon(s) supprimé(s) avec succès !`)
      } else {
        this.addLog('error', `⚠️ Nettoyage terminé avec des erreurs — ${globalSuccess} succès, ${globalError} erreur(s)`)
      }
    },

    /**
     * Formate une date ISO en "DD/MM/YYYY HH:mm:ss"
     */
    formatDateTime(dateStr) {
      if (!dateStr) return 'Date inconnue'
      return moment(dateStr).format('DD/MM/YYYY HH:mm:ss')
    },

    /**
     * Ajoute une entrée dans le journal
     */
    addLog(type, message) {
      const now = new Date()
      this.logs.unshift({
        type,
        message,
        time: now.toLocaleTimeString('fr-FR'),
        timestamp: now.getTime(),
      })
      // Limiter à 100 entrées
      if (this.logs.length > 100) {
        this.logs = this.logs.slice(0, 100)
      }
    },

    clearLogs() {
      this.logs = []
    },
  },
}
</script>
